import React, { useState, useEffect, useRef } from "react";
import styles from "./Dropdown.module.scss";
import Image from "next/image";

export interface DropdownOption {
    label: string;
    value: string;
    image?: string; // optional, for team flag images
}

interface DropdownProps {
    options: DropdownOption[];
    selectedOption: string;
    onChange: (value: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
    options,
    selectedOption,
    onChange,
}) => {
    const theme = localStorage.getItem("theme");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSelect = (option: DropdownOption) => {
        onChange(option.value);
        setIsOpen(false);
    };

    const currentOption = options.find(
        (option) => option.value === selectedOption,
    );

    return (
        <div
            className={styles.dropdownContainer}
            ref={dropdownRef}
            data-theme={theme}
        >
            <div
                className={styles.dropdownHeader}
                onClick={() => setIsOpen(!isOpen)}
                data-theme={theme}
            >
                {currentOption?.image && (
                    <Image
                        src={currentOption.image}
                        alt={currentOption.label}
                        className={styles.dropdownImage}
                        loading="eager"
                        width={20}
                        height={20}
                    />
                )}
                <div className={styles["dropdown-wrapper"]} data-theme={theme}>
                    {" "}
                    <span
                        className={styles.selectedDropdownLabel}
                        data-theme={theme}
                    >
                        {currentOption?.label}
                    </span>
                    <span className={styles.dropdownArrow}>
                        {isOpen ? "▲" : "▼"}
                    </span>
                </div>{" "}
            </div>
            {isOpen && (
                <div className={styles.dropdownList} data-theme={theme}>
                    {options.map((option) => (
                        <div
                            key={option.value}
                            className={styles.dropdownItem}
                            onClick={() => handleSelect(option)}
                            data-theme={theme}
                        >
                            {option.image && (
                                <Image
                                    src={option.image}
                                    alt={option.label}
                                    className={styles.dropdownImage}
                                    height={24}
                                    width={24}
                                    loading="eager"
                                />
                            )}
                            <span
                                className={styles.dropdownLabel}
                                data-theme={theme}
                            >
                                {option.label}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
